import React from 'react';
import { Sparkles, Play, Clock, Zap, Eye, MousePointer, Scroll } from 'lucide-react';

interface AnimationEditorProps {
  animations: Record<string, any>;
  onChange: (animations: Record<string, any>) => void;
}

export const AnimationEditor: React.FC<AnimationEditorProps> = ({ animations = {}, onChange }) => {
  const handleAnimationChange = (key: string, value: any) => {
    onChange({ ...animations, [key]: value });
  };

  const entranceAnimations = [
    { value: 'none', label: 'None' },
    { value: 'fadeIn', label: 'Fade In' },
    { value: 'slideUp', label: 'Slide Up' },
    { value: 'slideDown', label: 'Slide Down' },
    { value: 'slideLeft', label: 'Slide Left' },
    { value: 'slideRight', label: 'Slide Right' },
    { value: 'zoomIn', label: 'Zoom In' },
    { value: 'rotateIn', label: 'Rotate In' },
    { value: 'bounceIn', label: 'Bounce In' },
    { value: 'flipIn', label: 'Flip In' }
  ];

  const scrollAnimations = [
    { value: 'none', label: 'None' },
    { value: 'parallax', label: 'Parallax' },
    { value: 'reveal', label: 'Reveal on Scroll' },
    { value: 'sticky', label: 'Sticky' },
    { value: 'fadeOnScroll', label: 'Fade on Scroll' },
    { value: 'scaleOnScroll', label: 'Scale on Scroll' }
  ];

  const hoverEffects = [
    { value: 'none', label: 'None' },
    { value: 'grow', label: 'Grow' },
    { value: 'shrink', label: 'Shrink' },
    { value: 'rotate', label: 'Rotate' },
    { value: 'shadow', label: 'Shadow' },
    { value: 'lift', label: 'Lift Up' },
    { value: 'glow', label: 'Glow' },
    { value: 'blur', label: 'Blur Background' }
  ];

  const easingFunctions = [
    { value: 'linear', label: 'Linear' },
    { value: 'ease', label: 'Ease' },
    { value: 'ease-in', label: 'Ease In' },
    { value: 'ease-out', label: 'Ease Out' },
    { value: 'ease-in-out', label: 'Ease In Out' },
    { value: 'cubic-bezier(0.68,-0.55,0.265,1.55)', label: 'Bounce' }
  ];

  return (
    <div className="space-y-6">
      {/* Entrance Animation */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="w-4 h-4 text-blue-600" />
          <h3 className="text-sm font-medium">Entrance Animation</h3>
        </div>

        <div>
          <label className="block text-sm text-gray-600 mb-1">Animation Type</label>
          <select
            value={animations.entrance || 'none'}
            onChange={(e) => handleAnimationChange('entrance', e.target.value)}
            className="w-full px-3 py-2 border rounded-lg"
          >
            {entranceAnimations.map(anim => (
              <option key={anim.value} value={anim.value}>{anim.label}</option>
            ))}
          </select>
        </div>

        {animations.entrance && animations.entrance !== 'none' && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm text-gray-600 mb-1">Duration (ms)</label>
                <input
                  type="number"
                  value={animations.entranceDuration || 500}
                  onChange={(e) => handleAnimationChange('entranceDuration', parseInt(e.target.value))}
                  min={100}
                  max={3000}
                  step={100}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-600 mb-1">Delay (ms)</label>
                <input
                  type="number"
                  value={animations.entranceDelay || 0}
                  onChange={(e) => handleAnimationChange('entranceDelay', parseInt(e.target.value))}
                  min={0}
                  max={2000}
                  step={100}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm text-gray-600 mb-1">Easing</label>
              <select
                value={animations.entranceEasing || 'ease'}
                onChange={(e) => handleAnimationChange('entranceEasing', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
              >
                {easingFunctions.map(easing => (
                  <option key={easing.value} value={easing.value}>{easing.label}</option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="entranceOnce"
                checked={animations.entranceOnce || false}
                onChange={(e) => handleAnimationChange('entranceOnce', e.target.checked)}
                className="rounded"
              />
              <label htmlFor="entranceOnce" className="text-sm">
                Animate only once
              </label>
            </div>
          </>
        )}
      </div>

      {/* Scroll Animation */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 mb-2">
          <Scroll className="w-4 h-4 text-green-600" />
          <h3 className="text-sm font-medium">Scroll Animation</h3>
        </div>

        <div>
          <label className="block text-sm text-gray-600 mb-1">Scroll Effect</label>
          <select
            value={animations.scroll || 'none'}
            onChange={(e) => handleAnimationChange('scroll', e.target.value)}
            className="w-full px-3 py-2 border rounded-lg"
          >
            {scrollAnimations.map(anim => (
              <option key={anim.value} value={anim.value}>{anim.label}</option>
            ))}
          </select>
        </div>

        {animations.scroll === 'parallax' && (
          <div>
            <label className="block text-sm text-gray-600 mb-1">Parallax Speed</label>
            <input
              type="range"
              min="-100"
              max="100"
              value={animations.parallaxSpeed || 50}
              onChange={(e) => handleAnimationChange('parallaxSpeed', parseInt(e.target.value))}
              className="w-full"
            />
            <div className="text-center text-sm text-gray-600">
              {animations.parallaxSpeed || 50}%
            </div>
          </div>
        )}

        {animations.scroll === 'reveal' && (
          <div>
            <label className="block text-sm text-gray-600 mb-1">Reveal Threshold</label>
            <input
              type="range"
              min="0"
              max="100"
              value={animations.revealThreshold || 20}
              onChange={(e) => handleAnimationChange('revealThreshold', parseInt(e.target.value))}
              className="w-full"
            />
            <div className="text-center text-sm text-gray-600">
              {animations.revealThreshold || 20}% visible
            </div>
          </div>
        )}
      </div>

      {/* Hover Effects */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 mb-2">
          <MousePointer className="w-4 h-4 text-purple-600" />
          <h3 className="text-sm font-medium">Hover Effects</h3>
        </div>

        <div>
          <label className="block text-sm text-gray-600 mb-1">Hover Effect</label>
          <select
            value={animations.hover || 'none'}
            onChange={(e) => handleAnimationChange('hover', e.target.value)}
            className="w-full px-3 py-2 border rounded-lg"
          >
            {hoverEffects.map(effect => (
              <option key={effect.value} value={effect.value}>{effect.label}</option>
            ))}
          </select>
        </div>

        {animations.hover && animations.hover !== 'none' && (
          <div>
            <label className="block text-sm text-gray-600 mb-1">Hover Duration (ms)</label>
            <input
              type="number"
              value={animations.hoverDuration || 300}
              onChange={(e) => handleAnimationChange('hoverDuration', parseInt(e.target.value))}
              min={100}
              max={1000}
              step={50}
              className="w-full px-3 py-2 border rounded-lg"
            />
          </div>
        )}
      </div>

      {/* Click Animation */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 mb-2">
          <Zap className="w-4 h-4 text-orange-600" />
          <h3 className="text-sm font-medium">Click Animation</h3>
        </div>

        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="clickRipple"
            checked={animations.clickRipple || false}
            onChange={(e) => handleAnimationChange('clickRipple', e.target.checked)}
            className="rounded"
          />
          <label htmlFor="clickRipple" className="text-sm">
            Enable ripple effect on click
          </label>
        </div>

        {animations.clickRipple && (
          <div>
            <label className="block text-sm text-gray-600 mb-1">Ripple Color</label>
            <input
              type="color"
              value={animations.rippleColor || '#3B82F6'}
              onChange={(e) => handleAnimationChange('rippleColor', e.target.value)}
              className="w-full h-10 rounded cursor-pointer"
            />
          </div>
        )}
      </div>

      {/* Advanced Settings */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 mb-2">
          <Eye className="w-4 h-4 text-gray-600" />
          <h3 className="text-sm font-medium">Advanced Settings</h3>
        </div>

        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="disableOnMobile"
            checked={animations.disableOnMobile || false}
            onChange={(e) => handleAnimationChange('disableOnMobile', e.target.checked)}
            className="rounded"
          />
          <label htmlFor="disableOnMobile" className="text-sm">
            Disable animations on mobile devices
          </label>
        </div>

        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="respectReducedMotion"
            checked={animations.respectReducedMotion !== false}
            onChange={(e) => handleAnimationChange('respectReducedMotion', e.target.checked)}
            className="rounded"
          />
          <label htmlFor="respectReducedMotion" className="text-sm">
            Respect "prefers-reduced-motion" setting
          </label>
        </div>
      </div>

      {/* Preview Button */}
      <div className="pt-4 border-t">
        <button
          onClick={() => {
            // Trigger animation preview
            const event = new CustomEvent('previewAnimation', { detail: animations });
            window.dispatchEvent(event);
          }}
          className="w-full px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center justify-center gap-2"
        >
          <Play className="w-4 h-4" />
          Preview Animation
        </button>
      </div>
    </div>
  );
};